import {
  cn,
  Icon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@risksmart-app/atomic-ui';
import { useTranslation } from 'react-i18next';
import PropertyFilterPanel from 'src/components/property-filter-panel';

import { emptyFilterQuery } from '@/utils/table/types';

import { useGetWidgetData } from '../gigawidget/hooks/useGetWidgetData';
import type { WidgetDataSource } from '../gigawidget/types';
import styles from './style.module.scss';
import type { GigawidgetSettings } from './util';

type FilterPreviewProps = {
  dataSource: WidgetDataSource;
  settings: GigawidgetSettings;
  /**
   * When the widget is compact (small in either dimension — see `widgetCompact`),
   * the pills collapse into a floating filter icon so the chart reclaims the
   * space. This both keeps the pills usable (RSP-4068) and stops the donut count
   * label from being squeezed into the chart (RSP-4069).
   */
  isCompact: boolean;
};

export const FilterPreview = ({
  dataSource,
  settings,
  isCompact,
}: FilterPreviewProps) => {
  const { tableProps, loading } = useGetWidgetData({ dataSource });
  const { t } = useTranslation('common', { keyPrefix: 'dashboard' });

  const tokenCount = settings.filtering?.tokens?.length ?? 0;

  if (loading) {
    return null;
  }

  // The same read-only pills are reused verbatim in both layouts — inline when
  // roomy, inside the popover when compact.
  const pills = (
    <PropertyFilterPanel
      {...{ className: styles.filterPreview }}
      query={settings.filtering ?? emptyFilterQuery}
      filteringProperties={tableProps.filteringProperties}
      filteringOptions={tableProps.propertyFilterProps.filteringOptions}
      onChange={() => null}
      customControl={<></>}
      customFilterActions={<></>}
      hideOperations={false}
      disabled
      virtualScroll={true}
    />
  );

  if (!isCompact) {
    return pills;
  }

  // Compact: float the control out of flow so the pill row no longer consumes
  // the widget's vertical space (the chart reclaims it). Inset from the corner
  // to clear the board resize handle.
  return (
    <div className={cn('relative w-full')}>
      <div className={cn('absolute bottom-1 right-1 z-10')}>
        <Popover>
          <PopoverTrigger
            render={(triggerProps) => (
              <IconButton
                {...triggerProps}
                aria-label={t('show_filters', { count: tokenCount })}
                className={cn('border border-muted bg-neutral shadow-sm')}
              >
                <Icon name={'filter-funnel-01'} size={'sm'} />
              </IconButton>
            )}
          />
          <PopoverContent className={cn('max-w-md p-2')}>
            {pills}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
